import SimpleTooltip from "@rio-cloud/rio-uikit/SimpleTooltip";
import { useIntl } from "react-intl";
import { isOverdueCheckup } from "../../../services/periodicReviewsService";
import { Scheduling } from "../../../models/schedule";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { useState } from "react";
import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import { Button, ButtonDropdown, Dialog, RadioButton } from "@rio-cloud/rio-uikit";
import { getRevisionByIndex } from "../../../data/chassisReviewData";

interface ICardProps {
  scheduling: Scheduling;
  isOilChange?: boolean;
  index?: number;
  isSigned?: boolean;
  finished?: boolean;
  isEnhanced?: boolean;
  onHide?: () => void;
  isPreBook?: boolean;
  userRole?: 'TOWER' | 'CONSULTANT' | 'MANAGER' | 'PARTNERSHIP';
}

const Card = ({
  scheduling,
  isOilChange,
  index,
  isSigned,
  finished,
  isEnhanced,
  onHide,
  isPreBook,
  userRole,
}: ICardProps) => {
  const intl = useIntl();
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);

  const revIndex = (index ?? 0) + 2;
  const chassis = scheduling.asset?.chassis ?? "";
  const revision = isEnhanced ? getRevisionByIndex(chassis, revIndex) : undefined;
  const hasVolksEssentialsPlanContract = !!scheduling?.maintenanceData?.volksEssentialsPlanContract

  return (
    <div className={`border border-width-2 rounded-lg revision-card`}>
      <div
        className={`flex justify-between items-center mb-2 px-4 border-color-light border-b`}
      >
        <SimpleTooltip
          placement="top"
          textAlignment="left"
          content={scheduling.checkup?.name}
        >
          <p className="p-0 m-0 revision-card__title truncate">
            {scheduling.checkup?.name}
          </p>
        </SimpleTooltip>

        <div className="flex items-center">
          {hasVolksEssentialsPlanContract ? (
            <span className="badge badge-success">
              {intl.formatMessage({ id: "intl-msg:common.freeLabor" })}
            </span>
          ) : null}
          
          {isPreBook ? null : isEnhanced ? (
            <ButtonDropdown
              title={<span className="rioglyph rioglyph-option-vertical" />}
              items={[
                {
                  value: intl.formatMessage({ id: "intl-msg:periodicReviews.card.serviceDetails" }),
                  onSelect: () => setIsInfoDialogOpen(true),
                },
                {
                  value: intl.formatMessage({ id: "intl-msg:periodicReviews.card.hideReview" }),
                  onSelect: () => onHide?.(),
                },
              ]}
              bsStyle="muted"
              pullRight
              noCaret
              usePortal
              className="[&>button]:px-0 [&>button]:pl-2"
            />
          ) : (
            <span
              className={`ml-4 rioglyph rioglyph-eye-option ${
                scheduling.checkup?.services?.length
                  ? "cursor-pointer text-color-primary"
                  : "cursor-not-allowed"
              }`}
              onClick={() =>
                scheduling.checkup?.services?.length
                  ? setIsInfoDialogOpen(true)
                  : null
              }
            />
          )}
        </div>
      </div>

      {/* <div className="flex justify-between mb-2 text-lg p-4 border-b border-color-light">
        <span className="text-size-14 text-color-dark">
          {scheduling.checkup.odometer.toLocaleString('pt-Br')} Km
        </span>
        <span className="text-size-14 text-color-dark">{scheduling.checkup.hourmeter} H</span>
      </div> */}

      <>
        <div
          className={`flex justify-between gap-3 p-4 border-color-light border-b ${
            isMobile() ? "flex-wrap" : ""
          }`}
        >
          <div className="form-group w-[40%] min-w-0">
            <label>{intl.formatMessage({ id: "intl-msg:periodicReviews.osNumber" })}</label>
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling?.os : ""}
              className="form-control mt-1 p-2 border border-gray-300 dark:border-neutral-700 rounded-md text-gray-700 dark:text-neutral-300 input-sm"
            />
          </div>
          <div className="form-group w-[60%] min-w-0">
            <label>{intl.formatMessage({ id: "intl-msg:periodicReviews.date" })}</label>
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? scheduling.date?.toLocaleString(intl.locale, {
                      dateStyle: "short",
                      timeStyle: "short",
                    }) ?? ""
                  : ""
              }
              className="form-control mt-1 p-2 border border-gray-300 dark:border-neutral-700 rounded-md text-gray-700 dark:text-neutral-300 input-sm"
            />
          </div>
        </div>
        <div className="form-group flex flex-wrap justify-between p-4 field-section border-color-light border-b">
          <label className="block w-full text-size-12 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.hourmeter" })}
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? (scheduling.hourmeter ?? scheduling.maintenanceData?.hourmeter)?.toLocaleString(intl.locale) ?? ""
                  : ""
              }
              className="form-control mt-1 p-2 border border-gray-300 dark:border-neutral-700 rounded-md text-gray-700 dark:text-neutral-300"
            />
          </label>
          <label className="block w-full text-size-12 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.km" })}
            <input
              type="text"
              readOnly
              value={
                scheduling.isFinished
                  ? (scheduling.odometer ?? scheduling.maintenanceData?.odometer)?.toLocaleString(intl.locale) ?? ""
                  : ""
              }
              className="form-control mt-1 p-2 border border-gray-300 dark:border-neutral-700 rounded-md text-gray-700 dark:text-neutral-300"
            />
          </label>
        </div>

        <div className="form-group flex flex-wrap justify-between p-4 field-section border-color-light border-b">
          <label className="block w-full text-size-12 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.dealership" })}
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.dealership?.name : ""}
              className="form-control mt-1 p-2 border border-gray-300 dark:border-neutral-700 rounded-md text-gray-700 dark:text-neutral-300"
            />
          </label>
          <label className="block w-full text-size-12 text-color-dark">
            {intl.formatMessage({ id: "intl-msg:periodicReviews.consultant" })}
            <input
              type="text"
              readOnly
              value={scheduling.isFinished ? scheduling.maintenanceData?.consultant ?? scheduling.consultant : ""}
              className="form-control mt-1 p-2 border border-gray-300 dark:border-neutral-700 rounded-md text-gray-700 dark:text-neutral-300"
            />
          </label>
        </div>

        <div className="form-group flex flex-wrap p-4 field-section border-color-light border-b">
          {[
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.settling" }),
              value: "Assentamento",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.intermediate" }),
              value: "Intermediária"
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.lubrication" }),
              value: "L",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.mp1" }),
              value: "MP1",
            },
            {
              label: intl.formatMessage({ id: "intl-msg:periodicReviews.mp2" }),
              value: "MP2",
            },
          ].map((item) => (
             <RadioButton
              key={item.value}
              name={`checkup-type-${index}`}
              value={item.value}
              checked={(scheduling.maintenanceData?.checkupType ?? scheduling.checkup?.name) === item.value}
              readOnly
              className="text-left mr-8 mb-2"
            >
              <span className="text-size-14 text-color-dark">{item.label}</span>
            </RadioButton>
          ))}
        </div>

        <div className="flex items-center font-semibold text-color-primary text-teal-600 footer-card">
          {finished && !isSigned ? (
            <div
              className={`text-size-14 flex items-center gap-8 justify-center w-full`}
            >
              <span>{intl.formatMessage({ id: "intl-msg:periodicReviews.completed" })}</span>
              <img src="/assets/images/check-icon.svg" alt={intl.formatMessage({ id: "intl-msg:periodicReviews.card.checkIconAlt" })} />
            </div>
          ) : isSigned ? (
            <div
              className={`text-size-14 flex items-center gap-8 justify-center w-full`}
            >
              <span>
                {intl.formatMessage({ id: "intl-msg:periodicReviews.authenticated" }).split(" via ")[0]} <br /> {intl.formatMessage({ id: "intl-msg:periodicReviews.authenticated" }).split(" via ")[1] ? `via ${intl.formatMessage({ id: "intl-msg:periodicReviews.authenticated" }).split(" via ")[1]}` : ""}
              </span>
              <img
                src="/assets/images/blockchain-enabled.svg"
                alt={intl.formatMessage({ id: "intl-msg:periodicReviews.card.blockchainEnabledAlt" })}
              />
            </div>
          ) : (
            <div
              className={`text-size-14 flex items-center gap-8 justify-center w-full text-color-gray`}
            >
              <span>
                {intl.formatMessage({ id: "intl-msg:periodicReviews.awaitingAuth" }).split(" ")[0]} <br /> {intl.formatMessage({ id: "intl-msg:periodicReviews.awaitingAuth" }).split(" ")[1]}
              </span>
              <img
                src="/assets/images/blockchain-disabled.svg"
                alt={intl.formatMessage({ id: "intl-msg:periodicReviews.card.blockchainDisabledAlt" })}
              />
            </div>
          )}
        </div>
      </>

      <Dialog
        bsSize={Dialog.SIZE_SM}
        show={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
        title={scheduling.checkup?.name || ""}
        body={
          isEnhanced ? (
            <>
              <p className="text-size-14 font-semibold mb-4">
                {revision ? intl.formatMessage({ id: "intl-msg:shared.chassisReview.revisionLabel" }, { number: revision.revIndex - 1 }) : intl.formatMessage({ id: "intl-msg:periodicReviews.card.reviewNumber" }, { number: revIndex - 1 })}
                {revision?.km ? ` — ${revision.km.toLocaleString(intl.locale)} km` : ""}
                {revision?.repairDate ? ` · ${revision.repairDate}` : ""}
              </p>
              {revision?.parts?.[0]?.dealership && (
                <p className="text-size-12 text-color-gray mb-10">
                  {intl.formatMessage({ id: "intl-msg:periodicReviews.dealership" })} <span className="font-semibold text-color-dark">{revision.parts[0].dealership}</span>
                </p>
              )}
              {revision?.parts && revision.parts.length > 0 ? (
                <table className="table table-condensed w-full text-size-12">
                  <thead>
                    <tr>
                      {userRole !== 'MANAGER' && <th>{intl.formatMessage({ id: "intl-msg:periodicReviews.card.code" })}</th>}
                      <th>{intl.formatMessage({ id: "intl-msg:periodicReviews.card.description" })}</th>
                      <th>{intl.formatMessage({ id: "intl-msg:periodicReviews.card.quantity" })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revision.parts.map((part, i) => (
                      <tr key={i}>
                        {userRole !== 'MANAGER' && <td>{part.partNumber}</td>}
                        <td>{part.partName}</td>
                        <td>{part.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <span className="text-size-14 text-color-gray">{intl.formatMessage({ id: "intl-msg:periodicReviews.card.noPartsData" })}</span>
              )}
            </>
          ) : (
            <>
              <div className="text-size-16 mb-10">
                {scheduling.checkup?.odometer
                  ? scheduling.checkup?.odometer?.toLocaleString(intl.locale) + " Km"
                  : ""}
              </div>

              {scheduling.checkup?.services
                ?.map((service) => service.items)
                ?.flat()
                ?.map((service) => (
                  <div className="flex gap-4">
                    <img
                      src="/assets/images/check-select-icon.svg"
                      alt={intl.formatMessage({ id: "intl-msg:periodicReviews.card.checkIconAlt" })}
                    />
                    <span className="text-size-14 text-color-dark">
                      {service}
                    </span>
                  </div>
                ))}
            </>
          )
        }
        footer={
          <>
            <div className="display-flex justify-content-end">
              <Button
                classID="btn btn-default"
                onClick={() => setIsInfoDialogOpen(false)}
                className="margin-right-10"
              >
                {intl.formatMessage({ id: "intl-msg:periodicReviews.close" })}
              </Button>
            </div>
          </>
        }
      />
    </div>
  );
};

export default Card;
